import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const REGISTRATION_URL = 'https://cobbfootball.leagueapps.com/clubteams/4926021-2026-cherokee-youth-football--cheerleading';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If already on home, scroll directly. If on another page, navigate home with hash.
  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  // Handle hash scrolling after navigation lands on home page
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts: number) => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts > 0) {
          setTimeout(() => tryScroll(attempts - 1), 100);
        }
      };
      setTimeout(() => tryScroll(10), 100);
    }
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/3a284c370a15b2f5c58dd5f7c0a663aa.png" 
                alt="Cherokee Warriors Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-display text-xl text-warrior-black leading-none">CHEROKEE YOUTH</span>
              <span className="font-display text-lg text-warrior-red leading-none">FOOTBALL & CHEER</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors whitespace-nowrap cursor-pointer">
              HOME
            </button>
            <button onClick={() => scrollToSection('programs')} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors whitespace-nowrap cursor-pointer">
              PROGRAMS
            </button>
            <button onClick={() => { navigate('/events'); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors whitespace-nowrap cursor-pointer">
              EVENTS
            </button>
            <button onClick={() => { navigate('/gear'); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors whitespace-nowrap cursor-pointer">
              GEAR
            </button>
            <button onClick={() => { navigate('/sponsors'); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors whitespace-nowrap cursor-pointer">
              SPONSORS
            </button>
            <button onClick={() => scrollToSection('contact')} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors whitespace-nowrap cursor-pointer">
              CONTACT
            </button>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a
              href={REGISTRATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-warrior-red text-white font-sans font-bold text-sm px-6 py-3 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer inline-block"
            >
              REGISTER NOW
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-warrior-black cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors text-left cursor-pointer">
                HOME
              </button>
              <button onClick={() => scrollToSection('programs')} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors text-left cursor-pointer">
                PROGRAMS
              </button>
              <button onClick={() => { navigate('/events'); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors text-left cursor-pointer">
                EVENTS
              </button>
              <button onClick={() => { navigate('/gear'); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors text-left cursor-pointer">
                GEAR
              </button>
              <button onClick={() => { navigate('/sponsors'); setIsMobileMenuOpen(false); }} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors text-left cursor-pointer">
                SPONSORS
              </button>
              <button onClick={() => scrollToSection('contact')} className="font-sans text-sm font-semibold text-warrior-black hover:text-warrior-red transition-colors text-left cursor-pointer">
                CONTACT
              </button>
              <a
                href={REGISTRATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-warrior-red text-white font-sans font-bold text-sm px-6 py-3 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-center block"
              >
                REGISTER NOW
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
